import { wellnessService } from "@/services/wellnessService";
import { useAuth } from "@/contexts/AuthContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useEffect, useState } from "react";

export type EmotionChartProps = {
  data: ChartDataPoint[];
};

export type ChartDataPoint = {
  date: string;
  energy: number;
  focus: number;
  motivation: number;
};

// The EmotionChart component displays the chart with the provided data
const EmotionChart = ({ data }: EmotionChartProps) => {
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" stroke="#94a3b8" />
          <YAxis stroke="#94a3b8" />
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            labelStyle={{ fontWeight: "bold", marginBottom: "4px" }}
          />
          <Area
            type="monotone"
            dataKey="energy"
            stackId="1"
            stroke="#9b87f5"
            fill="#9b87f580"
          />
          <Area
            type="monotone"
            dataKey="focus"
            stackId="2"
            stroke="#7E9BEB"
            fill="#7E9BEB80"
          />
          <Area
            type="monotone"
            dataKey="motivation"
            stackId="3"
            stroke="#FF8A65"
            fill="#FF8A6580"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// EmotionChartWrapper handles data fetching and passes it to the EmotionChart component
const EmotionChartWrapper = () => {
  const { user: authUser } = useAuth();
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!authUser?.id) {
        setError("No user session found");
        setLoading(false);
        return;
      }

      try {
        // Note: userId is automatically extracted from the auth token on the backend
        const checkIns = await wellnessService.getCheckIns();
        const formattedData: ChartDataPoint[] = checkIns.map((checkIn: any) => {
          // Handle different date formats safely
          let localDate: Date;
          
          if (checkIn.date) {
            // Extract date string and parse it
            const dateStr = checkIn.date.split('T')[0]; // Get just the date part
            const [year, month, day] = dateStr.split('-').map(Number);
            
            // Validate date components
            if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
              localDate = new Date(year, month - 1, day); // month is 0-indexed
            } else {
              // Fallback to current date if parsing fails
              localDate = new Date();
            }
          } else {
            // Fallback to current date if no date provided
            localDate = new Date();
          }
          
          return {
            date: localDate.toLocaleString("en-US", {
              weekday: "short",
            }),
            energy: checkIn.energy || 0,
            focus: checkIn.focus || 0,
            motivation: checkIn.motivation || 0,
          };
        });
        setChartData(formattedData);
      } catch (err) {
        setError("Failed to fetch check-ins");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    console.log("Chart data:", chartData);
  }, [authUser?.id]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  // If no data, provide a meaningful empty state
  if (chartData.length === 0) {
    return (
      <div className="h-72 w-full flex items-center justify-center">
        <div className="text-center p-6">
          <p className="text-muted-foreground mb-2">No check-in data yet</p>
          <p className="text-sm text-muted-foreground">Start logging your daily check-ins to see your patterns emerge</p>
        </div>
      </div>
    );
  }

  return <EmotionChart data={chartData} />;
};

export default EmotionChartWrapper;
